import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { MessageCircle, AlertTriangle, CheckCircle2, Users, Loader2, X } from "lucide-react";

export function SupportPanel() {
  const { lang } = useLang();
  const [convs, setConvs] = useState([]);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [open, setOpen] = useState(null); // full conversation
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/support/conversations?limit=100&escalated_only=${escalatedOnly}`);
    setConvs(data.conversations || []);
  };

  useEffect(() => { load(); }, [escalatedOnly]);

  const openConv = async (sid) => {
    const { data } = await api.get(`/support/conversations/${sid}`);
    setOpen(data);
  };

  const resolve = async () => {
    if (!open) return;
    setBusy(true);
    try {
      await api.post(`/support/conversations/${open.session_id}/resolve`);
      toast.success(t(lang, "support_resolved"));
      setOpen(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const openCount = convs.filter((c) => c.escalated && !c.resolved_at).length;

  return (
    <div className="hq-card overflow-hidden" data-testid="support-panel">
      <div className="p-5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <MessageCircle size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-lg">{t(lang, "support_title")}</div>
        <span className="font-mono text-xs text-white/40">{convs.length}</span>
        {openCount > 0 && (
          <span className="font-mono text-[10px] uppercase text-[color:var(--hq-warning)] border border-[color:var(--hq-warning)]/40 px-2 py-0.5">
            {openCount} {t(lang, "support_escalated")}
          </span>
        )}
        <label className="ml-auto flex items-center gap-2 text-xs font-mono uppercase text-white/50 cursor-pointer">
          <input
            data-testid="support-filter-escalated"
            type="checkbox"
            checked={escalatedOnly}
            onChange={(e) => setEscalatedOnly(e.target.checked)}
          />
          {t(lang, "support_only_escalated")}
        </label>
      </div>

      <table className="hq-table">
        <thead>
          <tr>
            <th></th>
            <th>{t(lang, "support_col_customer")}</th>
            <th>{t(lang, "support_col_last")}</th>
            <th>{t(lang, "support_col_msgs")}</th>
            <th>{t(lang, "support_col_when")}</th>
          </tr>
        </thead>
        <tbody>
          {convs.map((c) => (
            <tr
              key={c.session_id}
              className="cursor-pointer hover:bg-white/5"
              onClick={() => openConv(c.session_id)}
              data-testid={`support-row-${c.session_id}`}
            >
              <td>
                {c.escalated ? (
                  <AlertTriangle size={12} className="text-[color:var(--hq-warning)]" />
                ) : (
                  <Users size={12} className="text-white/40" />
                )}
              </td>
              <td className="text-xs">
                {c.customer_email || <span className="text-white/40">{t(lang, "support_anonymous")}</span>}
              </td>
              <td className="text-xs text-white/70 max-w-[420px] truncate">{c.last_message}</td>
              <td className="font-mono text-xs">{c.messages_count}</td>
              <td className="font-mono text-[10px] text-white/40">
                {c.last_message_at ? new Date(c.last_message_at).toLocaleString() : "—"}
              </td>
            </tr>
          ))}
          {convs.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-8 font-mono text-xs text-white/40">
                {t(lang, "support_empty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="support-modal"
        >
          <div
            className="hq-card w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <div className="font-mono text-[10px] uppercase text-white/50">
                {open.session_id}
              </div>
              {open.escalated && (
                <span className="font-mono text-[10px] uppercase text-[color:var(--hq-warning)] border border-[color:var(--hq-warning)]/40 px-2">
                  {open.escalation_reason}
                </span>
              )}
              <button
                onClick={() => setOpen(null)}
                className="ml-auto text-white/50 hover:text-white p-1"
                data-testid="support-modal-close"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(open.messages || []).map((m, i) => (
                <div
                  key={i}
                  className={`text-sm ${m.role === "user" ? "text-white" : "text-[color:var(--hq-cyan)]"}`}
                >
                  <div className="font-mono text-[10px] uppercase text-white/40 mb-1">
                    {m.role} · {new Date(m.at).toLocaleTimeString()}
                  </div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2">
              {!open.resolved_at && (
                <button
                  data-testid="support-resolve-btn"
                  onClick={resolve}
                  disabled={busy}
                  className="hq-btn hq-btn-primary text-xs disabled:opacity-50"
                >
                  {busy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                  {t(lang, "support_resolve")}
                </button>
              )}
              {open.resolved_at && (
                <span className="font-mono text-[10px] uppercase text-[color:var(--hq-success)]">
                  {t(lang, "support_status_resolved")}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
